import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, LinearProgress, Chip, Stack, Button,
  Avatar, Tooltip, Skeleton
} from '@mui/material';
import SkillGraph from '../../components/LearningPath/SkillGraph';
import ProgressAnalytics from '../../components/LearningPath/ProgressAnalytics';
import MilestoneCelebration from '../../components/LearningPath/MilestoneCelebration';

const BG = '#09090b';
const CARD_SX = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '12px',
};
const T_PRIMARY = '#fafafa';
const T_SECONDARY = '#a1a1aa';
const T_MUTED = '#71717a';

const STATUS_STYLES = {
  completed:    { bg: '#10b98122', color: '#10b981', label: 'Done' },
  'in-progress':{ bg: '#6366f122', color: '#a5b4fc', label: 'In Progress' },
  available:    { bg: 'rgba(255,255,255,0.06)', color: T_SECONDARY, label: 'Available' },
  locked:       { bg: 'rgba(255,255,255,0.03)', color: T_MUTED, label: 'Locked' },
};

const DIFF_COLORS = { beginner: '#10b981', intermediate: '#f59e0b', advanced: '#ef4444', expert: '#8b5cf6' };

// ── Skill Timeline Item ───────────────────────────────────────────
const SkillTimelineItem = ({ skill, index, isLast }) => {
  const ss = STATUS_STYLES[skill.status] || STATUS_STYLES.available;
  const isCompleted = skill.status === 'completed';
  const isInProgress = skill.status === 'in-progress';

  return (
    <Box sx={{ display: 'flex', gap: 2, position: 'relative' }}>
      {/* Vertical line */}
      {!isLast && (
        <Box sx={{ position: 'absolute', left: 19, top: 40, bottom: -8, width: 2,
          bgcolor: isCompleted ? '#10b98133' : 'rgba(255,255,255,0.06)' }} />
      )}

      {/* Status dot */}
      <Box sx={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1,
        bgcolor: ss.bg, border: '2px solid ' + (isCompleted ? '#10b981' : isInProgress ? '#6366f1' : 'rgba(255,255,255,0.1)') }}>
        <Typography sx={{ fontSize: 18 }}>{skill.icon || '📚'}</Typography>
      </Box>

      {/* Content */}
      <Box sx={{ ...CARD_SX, flex: 1, p: 2.5, mb: 1.5,
        opacity: skill.status === 'locked' ? 0.5 : 1,
        '&:hover': skill.status !== 'locked' ? { borderColor: 'rgba(255,255,255,0.15)' } : {},
        transition: 'border-color .2s' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} color={T_PRIMARY}>{skill.name}</Typography>
            <Stack direction="row" gap={1} mt={0.5} flexWrap="wrap">
              <Chip label={ss.label} size="small"
                sx={{ bgcolor: ss.bg, color: ss.color, fontSize: 10, height: 18 }} />
              <Chip label={skill.difficulty} size="small"
                sx={{ bgcolor: (DIFF_COLORS[skill.difficulty] || T_MUTED) + '22',
                      color: DIFF_COLORS[skill.difficulty] || T_MUTED, fontSize: 10, height: 18 }} />
              <Typography variant="caption" color={T_MUTED}>⏱ {skill.estimatedHours}h</Typography>
            </Stack>
          </Box>
        </Box>

        {isInProgress && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color={T_MUTED}>Progress</Typography>
              <Typography variant="caption" color="#a5b4fc">{skill.proficiency}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={skill.proficiency || 0}
              sx={{ height: 4, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.06)',
                '& .MuiLinearProgress-bar': { bgcolor: '#6366f1', borderRadius: 4 } }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Milestone Badge ────────────────────────────────────────────────
const MilestoneBadge = ({ text, index }) => {
  const colors = ['#6366f1', '#10b981', '#f59e0b', '#06b6d4'];
  const c = colors[index % colors.length];
  return (
    <Box sx={{ ...CARD_SX, p: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5,
      borderColor: c + '33', '&:hover': { borderColor: c + '66' }, transition: 'border-color .2s' }}>
      <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: c + '22',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.3 }}>
        <Typography sx={{ fontSize: 14 }}>{index === 0 ? '🚀' : index === 1 ? '🌟' : index === 2 ? '⚡' : '🏆'}</Typography>
      </Box>
      <Typography variant="caption" color={T_SECONDARY} lineHeight={1.6}>{text}</Typography>
    </Box>
  );
};

// ── Main Component ─────────────────────────────────────────────────
const PathDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activePath, setActivePath] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [nextSkill, setNextSkill] = useState(null);
  const [completedSkills, setCompletedSkills] = useState([]);
  const [activeView, setActiveView] = useState('overview');
  const [showCelebration, setShowCelebration] = useState(false);
  const [currentMilestone, setCurrentMilestone] = useState(null);
  const [progressHistory, setProgressHistory] = useState([]);

  useEffect(() => { fetchPathData(); }, []);

  const fetchPathData = async () => {
    try {
      const token = localStorage.getItem('token');
      // TODO: Replace with actual API call when /active endpoint is ready
      await new Promise(resolve => setTimeout(resolve, 800));

      const mockPath = {
        targetRole: 'Renewable Energy Engineer',
        skills: [
          { _id: '5', name: 'Python Programming', icon: '🐍', estimatedHours: 40, difficulty: 'beginner', slug: 'python', status: 'completed', proficiency: 100, prerequisites: [] },
          { _id: '8', name: 'Sustainability Practices', icon: '🌱', estimatedHours: 15, difficulty: 'beginner', slug: 'sustainability', status: 'completed', proficiency: 100, prerequisites: [] },
          { _id: '6', name: 'Data Analysis', icon: '📊', estimatedHours: 30, difficulty: 'intermediate', slug: 'data-analysis', status: 'in-progress', proficiency: 65, prerequisites: ['5'] },
          { _id: '1', name: 'Solar Panel Design', icon: '☀️', estimatedHours: 30, difficulty: 'intermediate', slug: 'solar-panel-design', status: 'available', proficiency: 0, prerequisites: ['8'] },
          { _id: '2', name: 'Energy Storage Systems', icon: '🔋', estimatedHours: 25, difficulty: 'intermediate', slug: 'energy-storage', status: 'available', proficiency: 0, prerequisites: ['1'] },
          { _id: '7', name: 'Project Management', icon: '📋', estimatedHours: 20, difficulty: 'intermediate', slug: 'project-mgmt', status: 'locked', proficiency: 0, prerequisites: ['6'] },
          { _id: '3', name: 'Power Electronics', icon: '⚡', estimatedHours: 35, difficulty: 'advanced', slug: 'power-electronics', status: 'locked', proficiency: 0, prerequisites: ['2', '6'] },
          { _id: '4', name: 'Grid Integration', icon: '🔌', estimatedHours: 28, difficulty: 'advanced', slug: 'grid-integration', status: 'locked', proficiency: 0, prerequisites: ['3', '7'] },
        ],
        milestones: [
          'Month 1: Master foundational programming and data analysis',
          'Month 2: Understand solar panel design principles',
          'Month 3: Learn energy storage and power electronics',
          'Month 4: Integrate renewable systems with the grid'
        ],
        hoursPerWeek: 12,
        totalHours: 223,
        successProbability: 0.78,
        estimatedCompletion: { months: 5, weeks: 19 }
      };

      const mockProgressHistory = [
        { date: '2025-11-05', hoursStudied: 12, skillsCompleted: 1, quizScore: 85 },
        { date: '2025-11-10', hoursStudied: 13, skillsCompleted: 1, quizScore: 88 },
        { date: '2025-11-15', hoursStudied: 12, skillsCompleted: 0, quizScore: 95 },
        { date: '2025-11-20', hoursStudied: 13, skillsCompleted: 0, quizScore: 91 },
        { date: '2025-11-25', hoursStudied: 14, skillsCompleted: 0, quizScore: 93 },
      ];
      setProgressHistory(mockProgressHistory);
      setActivePath(mockPath);

      const mockProgress = [
        { skill: { _id: '5', name: 'Python Programming' }, proficiencyLevel: 4 },
        { skill: { _id: '8', name: 'Sustainability Practices' }, proficiencyLevel: 3 }
      ];
      setUserProgress(mockProgress);
      const completed = mockProgress.filter(m => m.proficiencyLevel >= 3);
      setCompletedSkills(completed);
      const completedIds = completed.map(c => c.skill._id);
      setNextSkill(mockPath.skills.find(s => !completedIds.includes(s._id)));
    } catch (error) {
      console.error('Error fetching path data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = () => {
    if (!activePath?.skills) return 0;
    return Math.round((completedSkills.length / activePath.skills.length) * 100);
  };

  const calculateTimeRemaining = () => {
    if (!activePath || !nextSkill) return { weeks: 0, months: 0 };
    const remainingSkills = activePath.skills.filter(s => !completedSkills.some(c => c.skill._id === s._id));
    const totalHours = remainingSkills.reduce((sum, s) => sum + (s.estimatedHours || 20), 0);
    const weeks = Math.ceil(totalHours / (activePath.hoursPerWeek || 12));
    return { weeks, months: Math.round(weeks / 4.33 * 10) / 10 };
  };

  const handleSkillClick = (skill) => navigate('/skills/' + (skill.slug || skill._id));

  useEffect(() => {
    if (!activePath) return;
    const progress = calculateProgress();
    const milestones = [
      { threshold: 25, name: 'First Quarter', message: "You've completed 25% of your journey!" },
      { threshold: 50, name: 'Halfway Point', message: "You're halfway there!" },
      { threshold: 75, name: 'Final Stretch', message: 'Only 25% left to go!' },
      { threshold: 100, name: 'Path Complete', message: "You've completed your learning path!" },
    ];
    const reached = milestones.find(m => progress >= m.threshold && progress < m.threshold + 5);
    if (reached) {
      setCurrentMilestone({
        name: reached.name, message: reached.message,
        skillsCompleted: completedSkills.length, totalSkills: activePath.skills.length,
        hoursCompleted: completedSkills.reduce((sum, c) => {
          const s = activePath.skills.find(sk => sk._id === c.skill._id);
          return sum + (s?.estimatedHours || 0);
        }, 0),
        progress, badge: progress === 100 ? '🏆' : progress >= 75 ? '⭐' : progress >= 50 ? '🌟' : '✨'
      });
      setShowCelebration(true);
    }
  }, [completedSkills]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: BG, p: { xs: 2, md: 4 } }}>
        <Skeleton width={260} height={36} sx={{ bgcolor: 'rgba(255,255,255,0.05)', mb: 2 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1,2,3,4].map(i => <Grid item xs={6} md={3} key={i}><Skeleton height={100} sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 3 }} /></Grid>)}
        </Grid>
        {[1,2,3,4].map(i => <Skeleton key={i} height={80} sx={{ bgcolor: 'rgba(255,255,255,0.05)', mb: 1.5, borderRadius: 3 }} />)}
      </Box>
    );
  }

  if (!activePath) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h1" sx={{ mb: 2 }}>🗺️</Typography>
          <Typography variant="h5" fontWeight={700} color={T_PRIMARY} mb={1}>No Active Learning Path</Typography>
          <Typography variant="body2" color={T_MUTED} mb={3}>Create a personalized learning path to get started!</Typography>
          <Button variant="contained" onClick={() => navigate('/create-path')}
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, borderRadius: 2 }}>
            Create Learning Path
          </Button>
        </Box>
      </Box>
    );
  }

  const progress = calculateProgress();
  const timeRemaining = calculateTimeRemaining();
  const skillsTotal = activePath.skills?.length || 0;
  const hoursInvested = completedSkills.reduce((sum, c) => {
    const s = activePath.skills?.find(sk => sk._id === c.skill._id);
    return sum + (s?.estimatedHours || 0);
  }, 0);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'graph', label: 'Skill Graph' },
    { id: 'analytics', label: 'Analytics' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: BG, color: T_PRIMARY, p: { xs: 2, md: 4 } }}>
      {showCelebration && currentMilestone && (
        <MilestoneCelebration milestone={currentMilestone} show={showCelebration}
          onClose={() => setShowCelebration(false)} />
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color={T_PRIMARY}>🎯 Your Learning Journey</Typography>
          <Typography variant="body2" color="#a5b4fc" mt={0.5}>{activePath.targetRole}</Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={() => navigate('/skills')}
          sx={{ borderColor: 'rgba(255,255,255,0.1)', color: T_SECONDARY, borderRadius: 2,
            '&:hover': { borderColor: 'rgba(255,255,255,0.2)' } }}>
          Browse All Skills
        </Button>
      </Box>

      {/* Progress Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Progress', value: progress + '%', sub: completedSkills.length + ' / ' + skillsTotal + ' skills', color: '#6366f1' },
          { label: 'Skills Mastered', value: completedSkills.length, sub: 'out of ' + skillsTotal, color: '#10b981' },
          { label: 'Hours Invested', value: hoursInvested + 'h', sub: 'of ~' + activePath.totalHours + 'h total', color: '#f59e0b' },
          { label: 'Est. Completion', value: timeRemaining.months + 'mo', sub: '~' + timeRemaining.weeks + ' weeks', color: '#06b6d4' },
        ].map(stat => (
          <Grid item xs={6} md={3} key={stat.label}>
            <Box sx={{ ...CARD_SX, p: 2.5 }}>
              <Typography variant="h5" fontWeight={700} color={stat.color}>{stat.value}</Typography>
              <Typography variant="body2" color={T_SECONDARY}>{stat.label}</Typography>
              <Typography variant="caption" color={T_MUTED}>{stat.sub}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Overall progress bar */}
      <Box sx={{ ...CARD_SX, p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color={T_SECONDARY}>Overall Progress</Typography>
          <Typography variant="body2" fontWeight={700} color="#a5b4fc">{progress}%</Typography>
        </Box>
        <LinearProgress variant="determinate" value={progress}
          sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.06)',
            '& .MuiLinearProgress-bar': { bgcolor: '#6366f1', borderRadius: 4 } }} />
        {activePath.successProbability && (
          <Typography variant="caption" color={T_MUTED} mt={0.5} display="block">
            Success probability: {Math.round(activePath.successProbability * 100)}%
          </Typography>
        )}
      </Box>

      {/* View Tabs */}
      <Stack direction="row" gap={1} mb={3}>
        {tabs.map(tab => (
          <Button key={tab.id} onClick={() => setActiveView(tab.id)} variant={activeView === tab.id ? 'contained' : 'outlined'}
            size="small"
            sx={activeView === tab.id
              ? { bgcolor: '#6366f1', color: '#fff', borderRadius: 2, '&:hover': { bgcolor: '#4f46e5' } }
              : { borderColor: 'rgba(255,255,255,0.1)', color: T_SECONDARY, borderRadius: 2,
                  '&:hover': { borderColor: 'rgba(255,255,255,0.2)' } }}>
            {tab.label}
          </Button>
        ))}
      </Stack>

      {/* Skill Graph view */}
      {activeView === 'graph' && (
        <Box sx={{ ...CARD_SX, p: 2 }}>
          <SkillGraph skills={activePath.skills} onSkillClick={handleSkillClick} />
        </Box>
      )}

      {/* Analytics view */}
      {activeView === 'analytics' && (
        <Box sx={{ ...CARD_SX, p: 2 }}>
          <ProgressAnalytics
            path={activePath}
            userProgress={userProgress}
            completedSkills={completedSkills}
            progressHistory={progressHistory}
          />
        </Box>
      )}

      {/* Overview */}
      {activeView === 'overview' && (
        <Grid container spacing={3}>
          {/* Timeline */}
          <Grid item xs={12} md={7}>
            <Box sx={{ ...CARD_SX, p: 3 }}>
              <Typography variant="h6" fontWeight={600} color={T_PRIMARY} mb={2.5}>Skill Timeline</Typography>
              {activePath.skills.map((skill, i) => (
                <SkillTimelineItem key={skill._id} skill={skill} index={i} isLast={i === activePath.skills.length - 1} />
              ))}
            </Box>
          </Grid>

          {/* Right panel */}
          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              {/* Next Action */}
              {nextSkill && (
                <Box sx={{ ...CARD_SX, p: 3, background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(99,102,241,0.04))' }}>
                  <Typography variant="caption" color="#a5b4fc" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                    Next Up
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 1.5 }}>
                    <Typography sx={{ fontSize: 28 }}>{nextSkill.icon || '📚'}</Typography>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} color={T_PRIMARY}>{nextSkill.name}</Typography>
                      <Typography variant="caption" color={T_MUTED}>⏱ {nextSkill.estimatedHours}h · {nextSkill.difficulty}</Typography>
                    </Box>
                  </Box>
                  <Button variant="contained" fullWidth
                    onClick={() => navigate('/skills/' + (nextSkill.slug || nextSkill._id))}
                    sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, borderRadius: 2 }}>
                    Start Learning
                  </Button>
                </Box>
              )}

              {/* Milestones */}
              <Box sx={{ ...CARD_SX, p: 3 }}>
                <Typography variant="h6" fontWeight={600} color={T_PRIMARY} mb={2}>Milestones</Typography>
                <Stack spacing={1.5}>
                  {activePath.milestones.map((m, i) => <MilestoneBadge key={i} text={m} index={i} />)}
                </Stack>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default PathDashboard;
